import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker'
import { reportError } from 'services/errorReportingService'
import type { AppStore } from '../../store'

type KnownPaymentErrorReasons =
  'accountTakeover' |
  'bedbankPriceChanged' |
  'bookingDateWarning' |
  'bridgerFailed' |
  'cantProcessOnlineOrder' |
  'flightsSessionExpired' |
  'generic' |
  'journeyPriceChanged' |
  'journeySoldOut' |
  'latitudeFailed' |
  'tourV2PriceChanged' |
  'travellerNameTooLong'

type CartTrackingEvent =
    'ADD_INSURANCE_PRESSED' |
    'DONT_ADD_INSURANCE_PRESSED' |
    'FLIGHT_SELECT_PRESSED' |
    'TRANSACTION_FAILED' |
    'TRANSACTION_STARTED' |
    'TRANSACTION_SUCCEEDED' |
    'DEPARTING_FLIGHT_PAGE_VIEWED' |
    'RETURN_FLIGHT_PAGE_VIEWED' |
    'CONTINUE_TO_EXPERIENCE_UPSELL_PRESSED' |
    'EXPERIENCE_PAGE_VIEWED' |
    'CONTINUE_TO_CHECKOUT_PRESSED' |
    'SKIP_EXPERIENCE_UPSELL' |
    'REVIEW_PAYMENT_PAGE_VIEWED' |
    'PAYMENT_METHOD_PRESSED' |
    'PAY_PRESSED' |
    'CONFIRM_TRAVELLER_DETAILS_MODAL_VIEWED' |
    'CONFIRM_TRAVELLER_DETAILS_PRESSED' |
    'BOOKING_CONFIRMED_VIEWED' |
    'VIEW_RETURN_FLIGHTS_PRESSED'

const CATEGORIES = {
  InflowDepartingFlight: 'inflow_departing_flight',
  InflowExperiencesPage: 'inflow_experiences_page',
  ReviewPayment: 'review_payment',
  BookingConfirmed: 'booking_confirmed',
}

const eventsMap: Record<CartTrackingEvent, comluxgroup.ClientEvent_1_0_0> = {
  FLIGHT_SELECT_PRESSED: {
    action: 'pressed',
    subject: 'flight_select',
    type: 'interaction',
    category: CATEGORIES.InflowDepartingFlight,
  },
  DEPARTING_FLIGHT_PAGE_VIEWED: {
    action: 'viewed',
    subject: 'departing_flight_page',
    type: 'nonInteraction',
    category: CATEGORIES.InflowDepartingFlight,
  },
  VIEW_RETURN_FLIGHTS_PRESSED: {
    action: 'pressed',
    subject: 'view_return_flights',
    type: 'interaction',
    category: CATEGORIES.InflowDepartingFlight,
  },
  RETURN_FLIGHT_PAGE_VIEWED: {
    action: 'viewed',
    subject: 'return_flight_page',
    type: 'nonInteraction',
    category: CATEGORIES.InflowDepartingFlight,
  },
  CONTINUE_TO_EXPERIENCE_UPSELL_PRESSED: {
    action: 'pressed',
    subject: 'continue',
    type: 'interaction',
    category: CATEGORIES.InflowDepartingFlight,
  },
  EXPERIENCE_PAGE_VIEWED: {
    action: 'viewed',
    subject: 'experiences_page',
    type: 'nonInteraction',
    category: CATEGORIES.InflowExperiencesPage,
  },
  CONTINUE_TO_CHECKOUT_PRESSED: {
    action: 'pressed',
    subject: 'review_payment',
    type: 'interaction',
    category: CATEGORIES.InflowExperiencesPage,
  },
  SKIP_EXPERIENCE_UPSELL: {
    action: 'pressed',
    subject: 'skip',
    type: 'interaction',
    category: CATEGORIES.InflowExperiencesPage,
  },
  REVIEW_PAYMENT_PAGE_VIEWED: {
    action: 'viewed',
    subject: 'review_payment_page',
    type: 'nonInteraction',
    category: CATEGORIES.ReviewPayment,
  },
  ADD_INSURANCE_PRESSED: {
    action: 'pressed',
    subject: 'add_insurance',
    type: 'interaction',
    category: CATEGORIES.ReviewPayment,
  },
  DONT_ADD_INSURANCE_PRESSED: {
    action: 'pressed',
    subject: 'dont_add_insurance',
    type: 'interaction',
    category: CATEGORIES.ReviewPayment,
  },
  PAYMENT_METHOD_PRESSED: {
    action: 'pressed',
    subject: 'payment_method',
    type: 'interaction',
    category: CATEGORIES.ReviewPayment,
  },
  PAY_PRESSED: {
    action: 'pressed',
    subject: 'pay',
    type: 'interaction',
    category: CATEGORIES.ReviewPayment,
  },
  CONFIRM_TRAVELLER_DETAILS_MODAL_VIEWED: {
    action: 'viewed',
    subject: 'confirm_traveller_details',
    type: 'nonInteraction',
    category: CATEGORIES.ReviewPayment,
  },
  CONFIRM_TRAVELLER_DETAILS_PRESSED: {
    action: 'pressed',
    subject: 'confirm_traveller_details',
    type: 'interaction',
    category: CATEGORIES.ReviewPayment,
  },
  TRANSACTION_STARTED: {
    action: 'started',
    subject: 'transaction',
    type: 'nonInteraction',
    category: CATEGORIES.ReviewPayment,
  },
  TRANSACTION_FAILED: {
    action: 'failed',
    subject: 'transaction',
    type: 'nonInteraction',
    category: CATEGORIES.ReviewPayment,
  },
  TRANSACTION_SUCCEEDED: {
    action: 'started',
    subject: 'transaction',
    type: 'nonInteraction',
    category: CATEGORIES.ReviewPayment,
  },
  BOOKING_CONFIRMED_VIEWED: {
    action: 'viewed',
    subject: 'booking_confirmed',
    type: 'nonInteraction',
    category: CATEGORIES.BookingConfirmed,
  },
}

function buildJourneyFlight(journeyFlight: App.JourneyFlight | null): comluxgroup.CartContext_1_0_0['flight']['journey']['departing'] | comluxgroup.CartContext_1_0_0['flight']['journey']['returning'] {
  if (!journeyFlight) {
    return null
  }
  const flightTime = {
    hours: journeyFlight.flightTime?.hours,
    minutes: journeyFlight.flightTime?.minutes,
  }
  const priceBreakdown = {
    adults: {
      cost: journeyFlight.priceBreakdown?.adults?.cost,
      tax: journeyFlight.priceBreakdown?.adults?.tax,
    },
    children: {
      cost: journeyFlight.priceBreakdown?.children?.cost,
      tax: journeyFlight.priceBreakdown?.children?.tax,
    },
    infants: {
      cost: journeyFlight.priceBreakdown?.infants?.cost,
      tax: journeyFlight.priceBreakdown?.infants?.tax,
    },
  }

  const flights = (journeyFlight.flights ?? []).map(flight => ({
    departingAirport: flight.departingAirport,
    departingDate: flight.departingDate,
    departingTime: flight.departingTime,
    arrivalAirport: flight.arrivalAirport,
    arrivalDate: flight.arrivalDate,
    arrivalTime: flight.arrivalTime,
    carrierName: flight.carrierName,
    fareClass: flight.fareClass,
    layoverTime: {
      hours: flight.layoverTime?.hours,
      minutes: flight.layoverTime?.minutes,
    },
  }))

  return {
    journeyKey: journeyFlight.journeyKey,
    flightTime,
    priceBreakdown,
    flights,
  }
}

function buildCartContext(cartState: App.CartState, paymentType?: string, paymentErrorReason?: KnownPaymentErrorReasons): {
  schema: string;
  data: Record<string, unknown>;
} {
  const items = (cartState.items ?? []).map((item) => ({
    id: item.id,
    orderItemId: item.orderItemId,
    price: item.price,
    value: item.value,
    type: item.type,
    status: item.status,
  }))
  const flight: comluxgroup.CartContext_1_0_0['flight'] = {
    journey: {
      id: cartState.flight?.journey?.id,
      searchId: cartState.flight?.journey?.searchId,
      fareClass: cartState.flight?.journey?.fareClass,
      fareType: cartState.flight?.journey?.fareType,
      departing: buildJourneyFlight(cartState.flight?.journey?.departing),
      returning: buildJourneyFlight(cartState.flight?.journey?.returning),
    },
    passengers: (cartState.flight?.passengers ?? []).map(passenger => ({
      isPrimary: !!passenger.isPrimary,
      type: passenger.type,
    })),
  }
  const insurance = {
    transactionKey: cartState.insurance?.transactionKey,
    insuranceAccepted: cartState.insurance?.insuranceAccepted,
    insuranceProductId: cartState.insurance?.insuranceProductId,
  }
  return comluxgroup.createCartContext_1_0_0({
    paymentType,
    paymentErrorReason,
    transactionKey: cartState.transactionKey,
    items,
    currencyCode: cartState.currencyCode,
    flight,
    insurance,
  })
}

let _store: AppStore | undefined

export function registerCartTrackingStore(store: AppStore) {
  _store = store
}

interface Extras {
  paymentType?: string | null;
  errorReason?: KnownPaymentErrorReasons;
}

export function trackCartEvent(eventType: CartTrackingEvent, extras: Extras = {}) {
  try {
    const { paymentType, errorReason } = extras
    if (!_store) {
      throw new Error('No redux registered for tracking cart snowplow events')
    }
    const cartState: App.CartState = _store.getState().cart
    const eventData = eventsMap[eventType]
    const evt = {
      context: [buildCartContext(cartState, paymentType, errorReason)],
      event: comluxgroup.createClientEvent_1_0_0({
        ...eventData,
      }),
    }

    trackSelfDescribingEvent(evt)
  } catch (e) {
    reportError(e)
  }
}
